import { Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useGetUserByEmailQuery, useResendInvitationMutation } from '@/api/v1';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly error?: 'user.conflict' | 'activeUser.conflict' | null;
  readonly email: string;
}

export const UserFormEmailGlobalError: React.FC<Props> = (
  props: Props,
): React.ReactElement | null => {
  const [resendInvitation, mutationStatus] = useResendInvitationMutation();

  const { error, email } = props;
  const { data: user } = useGetUserByEmailQuery(email);

  const tr = useTranslator();
  const toast = useToast();
  const triggerResend = React.useCallback(async (): Promise<void> => {
    if (!user) {
      console.warn('User is not logged in');
      return;
    }

    try {
      await resendInvitation(user.id).unwrap();
      toast.show(tr('RESEND_INVITE_SUCCESS'), 'success');
    } catch (error: unknown) {
      console.error(error);
      toast.show(tr('RESEND_INVITE_ERROR'), 'error');
    }
  }, [user, resendInvitation, toast, tr]);

  const link = React.useMemo((): React.ReactElement | null => {
    const color = mutationStatus.status === QueryStatus.pending ? 'custom.LightGray' : 'primary';

    if (error === 'user.conflict') {
      return (
        <Link color={color} onClick={triggerResend}>
          {tr('RESEND_INVITE')}
        </Link>
      );
    } else {
      return null;
    }
  }, [error, mutationStatus.status, tr, triggerResend]);

  if (!error) {
    return null;
  }

  return (
    <FormHelperText sx={styles.errorMessage} error={true}>
      <Typography component="span">{tr(errorMessages[error])}.</Typography> {link}
    </FormHelperText>
  );
};

const styles: Record<string, SystemStyleObject> = {
  spinner: {
    mx: 1,
  },
  errorMessage: {
    lineHeight: '24px',
    mt: 0,
  },
};

const errorMessages: Record<'user.conflict' | 'activeUser.conflict', string> = {
  'user.conflict': 'VALIDATION_USER_CONFLICT',
  'activeUser.conflict': 'VALIDATION_ACTIVE_USER_CONFLICT',
};
